/**
 * ImportContourUtils.js
 *
 * Utilities for importing fire contour geometries from GeoJSON and KML files.
 *
 * Steps:
 *  1. Read the file (GeoJSON or KML)
 *  2. Extract the first Polygon / MultiPolygon feature
 *  3. Detect the source CRS  (explicit `crs` member → coordinate heuristic → default 4326)
 *  4. Reproject to EPSG:4326 with proj4-fully-loaded  (no-op if already 4326)
 *  5. Validate the resulting ring has at least 4 coordinates
 *  6. Return { feature, wasReprojected, detectedEpsg }
 *
 * Install:
 *   npm install proj4-fully-loaded
 */

import proj4 from "proj4-fully-loaded";

// ---------------------------------------------------------------------------
// CRS detection
// ---------------------------------------------------------------------------

/**
 * Try to read the EPSG code from a GeoJSON `crs` member.
 *
 * Handles both:
 *   { "type": "name", "properties": { "name": "EPSG:2154" } }
 *   { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::2154" } }
 */
const epsgFromCrsMember = (json) => {
  const crs = json?.crs;
  if (!crs) return null;
  if (crs.type === "name") {
    const name = crs.properties?.name || "";
    const match = name.match(/EPSG[::]+(\d+)/i);
    if (match) return `EPSG:${match[1]}`;
  }
  return null;
};

/**
 * Heuristic: guess the projection from the magnitude of the first coordinate.
 *
 * KEY CHANGE vs previous version:
 * We no longer guess a specific UTM zone from coordinates alone — it is
 * impossible to know the correct zone without metadata, and guessing wrong
 * (e.g. 32631 when the file is actually 32632) produces a badly distorted
 * polygon that the backend rejects.
 *
 * UTM files without a `crs` member will fall through to the "unknown
 * projected" branch and return null, which makes the caller throw a clear
 * user-facing error asking them to ensure the file has a CRS declaration.
 *
 * Returns an EPSG string, or null when coordinates look like degrees or
 * when the zone cannot be determined safely.
 */
const guessEpsgFromCoordinates = (coord) => {
  const [x, y] = coord;

  // Lambert-93 (France national grid) — range is unambiguous
  // x: ~100 000 – 1 300 000   y: ~6 000 000 – 7 200 000
  if (x > 100_000 && x < 1_300_000 && y > 6_000_000 && y < 7_200_000) {
    return "EPSG:2154";
  }

  // Web Mercator — x/y both large but within ±20 037 508 and not degrees
  if (
    Math.abs(x) > 180 &&
    Math.abs(x) <= 20_100_000 &&
    Math.abs(y) > 90 &&
    Math.abs(y) <= 20_100_000
  ) {
    return "EPSG:3857";
  }

  // Legacy NTF Lambert zones (France) — y values are distinctive per zone
  // Zone 1: y ~ 1 000 000 – 1 400 000
  if (x > 50_000 && x < 1_100_000 && y > 1_000_000 && y < 1_400_000) {
    return "EPSG:27561";
  }
  // Zone 2: y ~ 2 000 000 – 2 400 000
  if (x > 50_000 && x < 1_100_000 && y > 2_000_000 && y < 2_400_000) {
    return "EPSG:27562";
  }
  // Zone 3: y ~ 3 000 000 – 3 400 000
  if (x > 50_000 && x < 1_100_000 && y > 3_000_000 && y < 3_400_000) {
    return "EPSG:27563";
  }
  // Zone 4: y ~ 4 100 000 – 4 300 000
  if (x > 50_000 && x < 1_100_000 && y > 4_100_000 && y < 4_300_000) {
    return "EPSG:27564";
  }

  // Generic UTM northern hemisphere:
  // x easting: 100 000 – 900 000  (always, regardless of zone)
  // y northing: 0 – 10 000 000    (northern hemisphere)
  // We CAN determine the UTM zone from the longitude once we know it is UTM,
  // but we do not have the longitude yet at this stage.
  // Instead we return a special sentinel so the caller knows it looks like
  // UTM but we cannot determine the zone — the file MUST have a crs member.
  if (x > 100_000 && x < 900_000 && y > 0 && y < 10_100_000) {
    return "UNKNOWN_UTM";
  }

  // Coordinates look like decimal degrees already
  return null;
};

/**
 * Determine the source EPSG for a GeoJSON object.
 *
 * Priority:
 *   1. Explicit `crs` member in the file  (always trusted)
 *   2. Coordinate magnitude heuristic
 *   3. Default → EPSG:4326
 */
const detectEpsg = (json, firstCoord) => {
  // Always trust an explicit crs member — this is the only reliable source
  // for UTM zone discrimination (32630 vs 32631 vs 32632 etc.)
  const fromMember = epsgFromCrsMember(json);
  if (fromMember) return fromMember;

  if (firstCoord) {
    const fromHeuristic = guessEpsgFromCoordinates(firstCoord);
    if (fromHeuristic) return fromHeuristic;
  }

  return "EPSG:4326";
};

// ---------------------------------------------------------------------------
// Coordinate reprojection
// ---------------------------------------------------------------------------

/**
 * Reproject a single [x, y] pair from srcEpsg to EPSG:4326.
 * Returns [longitude, latitude].
 */
const reprojectCoord = (coord, srcEpsg) => {
  if (srcEpsg === "EPSG:4326") return coord;
  const [lon, lat] = proj4(srcEpsg, "EPSG:4326", [coord[0], coord[1]]);
  return [lon, lat];
};

/**
 * Reproject every coordinate ring of a GeoJSON Polygon geometry.
 * Returns a new geometry object (does not mutate the original).
 */
const reprojectPolygonGeometry = (geometry, srcEpsg) => {
  if (srcEpsg === "EPSG:4326") return geometry;
  return {
    ...geometry,
    coordinates: geometry.coordinates.map((ring) =>
      ring.map((coord) => reprojectCoord(coord, srcEpsg))
    ),
  };
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Parse a GeoJSON (.geojson / .json) file.
 *
 * Automatically detects the CRS, reprojects to EPSG:4326 if needed,
 * validates the ring, and returns the first polygon feature.
 *
 * @param   {File} file
 * @returns {Promise<{ feature, wasReprojected, detectedEpsg }>}
 */
export const parseGeoJSONFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);

        const rawFeatures = extractPolygonFeatures(json);
        if (rawFeatures.length === 0) {
          reject(
            new Error(
              "Aucun polygone trouvé dans le fichier GeoJSON. " +
                "Assurez-vous que le fichier contient un polygone."
            )
          );
          return;
        }

        // CRS is detected once from the file, applied to every polygon part
        const firstCoord = rawFeatures[0].geometry.coordinates[0]?.[0];
        const detectedEpsg = detectEpsg(json, firstCoord);

        if (detectedEpsg === "UNKNOWN_UTM") {
          reject(
            new Error(
              "Le fichier semble être en projection UTM mais ne contient pas " +
                "de déclaration de système de coordonnées (CRS/SRID). " +
                "Veuillez ré-exporter le fichier en incluant la projection " +
                "(ex. EPSG:32631) ou en WGS 84 (EPSG:4326)."
            )
          );
          return;
        }

        const wasReprojected = detectedEpsg !== "EPSG:4326";

        const features = [];
        for (const rawFeature of rawFeatures) {
          let reprojectedGeometry;
          try {
            reprojectedGeometry = reprojectPolygonGeometry(
              rawFeature.geometry,
              detectedEpsg
            );
          } catch (projErr) {
            reject(
              new Error(
                `Échec de la reprojection depuis ${detectedEpsg} : ${projErr.message}. ` +
                  "Vérifiez que le fichier contient une projection valide."
              )
            );
            return;
          }

          const feature = normaliseFeature({
            ...rawFeature,
            geometry: reprojectedGeometry,
          });

          const invalidRing = feature.geometry.coordinates.find(
            (ring) => ring.length < 4
          );
          if (invalidRing) {
            reject(
              new Error(
                "Un des polygones importés est invalide après reprojection " +
                  "(anneau avec moins de 4 coordonnées). " +
                  "Vérifiez que le fichier contient des polygones valides " +
                  "et que la projection déclarée est correcte."
              )
            );
            return;
          }

          features.push(feature);
        }

        resolve({ features, wasReprojected, detectedEpsg });
      } catch {
        reject(
          new Error(
            "Fichier GeoJSON invalide. Vérifiez que le fichier est bien formaté."
          )
        );
      }
    };

    reader.onerror = () => reject(new Error("Impossible de lire le fichier."));
    reader.readAsText(file);
  });
};
/**
 * Parse a KML (.kml) file.
 * KML always uses EPSG:4326 by spec, so no reprojection is needed.
 *
 * @param   {File} file
 * @returns {Promise<{ feature, wasReprojected, detectedEpsg }>}
 */
export const parseKMLFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(e.target.result, "text/xml");

        const parseError = kmlDoc.querySelector("parsererror");
        if (parseError) {
          reject(
            new Error(
              "Fichier KML invalide. Vérifiez que le fichier est bien formaté."
            )
          );
          return;
        }

        // 👇 plural now, mirrors extractPolygonFeatures for GeoJSON
        const features = extractPolygonsFromKML(kmlDoc);
        if (features.length === 0) {
          reject(
            new Error(
              "Aucun polygone trouvé dans le fichier KML. " +
                "Assurez-vous que le fichier contient un polygone."
            )
          );
          return;
        }

        // Validate ring size across every extracted polygon
        for (const feature of features) {
          const invalidRing = feature.geometry.coordinates.find(
            (ring) => ring.length < 4
          );
          if (invalidRing) {
            reject(
              new Error(
                "Un des polygones KML importés est invalide (anneau avec moins de 4 coordonnées)."
              )
            );
            return;
          }
        }

        resolve({ features, wasReprojected: false, detectedEpsg: "EPSG:4326" });
      } catch (err) {
        reject(
          new Error(
            "Erreur lors du traitement du fichier KML : " + err.message
          )
        );
      }
    };

    reader.onerror = () => reject(new Error("Impossible de lire le fichier."));
    reader.readAsText(file);
  });
};

/**
 * Main entry point.
 * Detects file type, delegates to the right parser,
 * and always resolves with EPSG:4326 coordinates.
 *
 * @param   {File} file
 * @returns {Promise<{ feature, wasReprojected, detectedEpsg }>}
 */
export const parseContourFile = (file) => {
  const name = file.name.toLowerCase();

  if (name.endsWith(".geojson") || name.endsWith(".json")) {
    return parseGeoJSONFile(file);
  }
  if (name.endsWith(".kml")) {
    return parseKMLFile(file);
  }
  return Promise.reject(
    new Error(
      "Format de fichier non supporté. " +
        "Utilisez un fichier GeoJSON (.geojson, .json) ou KML (.kml)."
    )
  );
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Walk any GeoJSON structure and return ALL Polygon features found.
 * MultiPolygon → one Feature per polygon part (not just the biggest one).
 */
const extractPolygonFeatures = (json) => {
  const candidates = [];

  if (json.type === "FeatureCollection") {
    candidates.push(...(json.features || []));
  } else if (json.type === "Feature") {
    candidates.push(json);
  } else if (json.type === "Polygon" || json.type === "MultiPolygon") {
    candidates.push({ type: "Feature", geometry: json, properties: {} });
  }

  const results = [];

  for (const f of candidates) {
    const geom = f.geometry;
    if (!geom) continue;

    if (geom.type === "Polygon") {
      results.push(normaliseFeature(f));
    }

    if (geom.type === "MultiPolygon") {
      // geom.coordinates is an array of polygons — keep every one of them
      geom.coordinates.forEach((polygonRings) => {
        results.push(
          normaliseFeature({
            type: "Feature",
            properties: f.properties || {},
            geometry: { type: "Polygon", coordinates: polygonRings },
          }),
        );
      });
    }
  }

  return results;
};
/**
 * Ensure the outer ring is closed and strip any non-serialisable properties
 * that could confuse Mapbox Draw.
 *
 * IMPORTANT: closing is done by strict value comparison on the raw numbers,
 * NOT on rounded values.  Two coords that happen to be equal after floating-
 * point reprojection are already closed; if they differ by even 1 ULP we add
 * the closing point.  This avoids accidentally producing a 3-coord ring.
 */
const normaliseFeature = (feature) => {
  const coords = feature.geometry.coordinates.map((ring) => {
    // Remove duplicate consecutive points that can appear after reprojection
    const deduped = ring.filter(
      (coord, i) =>
        i === 0 ||
        coord[0] !== ring[i - 1][0] ||
        coord[1] !== ring[i - 1][1]
    );

    // Close the ring if not already closed
    const first = deduped[0];
    const last  = deduped[deduped.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      deduped.push([...first]);
    }

    return deduped;
  });

  return {
    type: "Feature",
    properties: {},
    geometry: { type: "Polygon", coordinates: coords },
  };
};

/**
 * Extract the first Polygon from a KML DOM document.
 */
const extractPolygonsFromKML = (kmlDoc) => {
  const polygonElements = kmlDoc.getElementsByTagName("Polygon");
  const features = [];

  for (const polygonEl of polygonElements) {
    const coordinates = parseKMLPolygonCoordinates(polygonEl);
    if (!coordinates) continue;

    features.push(
      normaliseFeature({
        type: "Feature",
        properties: {},
        geometry: { type: "Polygon", coordinates },
      }),
    );
  }

  return features;
};

/**
 * Parse outer + inner rings from a KML <Polygon> element.
 * Returns [[outerRing, ...innerRings]] or null.
 */
const parseKMLPolygonCoordinates = (polygonEl) => {
  const rings = [];

  const outerBoundary = polygonEl.getElementsByTagName("outerBoundaryIs")[0];
  if (!outerBoundary) return null;

  const outerCoords = parseKMLCoordinateString(
    outerBoundary.getElementsByTagName("coordinates")[0]?.textContent
  );
  if (!outerCoords) return null;
  rings.push(outerCoords);

  const innerBoundaries = polygonEl.getElementsByTagName("innerBoundaryIs");
  for (const inner of innerBoundaries) {
    const innerCoords = parseKMLCoordinateString(
      inner.getElementsByTagName("coordinates")[0]?.textContent
    );
    if (innerCoords) rings.push(innerCoords);
  }

  return rings;
};

/**
 * Parse a KML coordinate string ("lon,lat,alt lon,lat,alt …") into
 * a GeoJSON coordinate array [[lon, lat], …].
 * Altitude is dropped.
 */
const parseKMLCoordinateString = (coordString) => {
  if (!coordString) return null;

  const coords = coordString
    .trim()
    .split(/\s+/)
    .map((pair) => {
      const parts = pair.split(",").map(Number);
      if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
      return [parts[0], parts[1]];
    })
    .filter(Boolean);

  return coords.length >= 3 ? coords : null;
};